import axios from "axios";
import API from "../../../api/baseApi";

const fetchFormApi = async (apiData) => {
  // If user provided a raw cURL string, parse and execute it. Otherwise
  // fall back to legacy URL/method/header/payload fields.
  const curl = apiData?.cURL || apiData?.CURL;

  let axiosConfig = null;

  if (curl && typeof curl === "string" && curl.trim()) {
    // Basic cURL parser: extract method, url, headers, data
    const methodMatch = curl.match(/-X\s+([A-Z]+)/i) || curl.match(/--request\s+([A-Z]+)/i);
    let method = methodMatch ? methodMatch[1].toLowerCase() : undefined;

    // try to infer POST if data flags present
    if (!method && /(--data|-d|--data-raw|--data-binary)/i.test(curl)) method = "post";
    if (!method) method = "get";

    const urlMatch = curl.match(/https?:\/\/[^"'\s)]+/i);
    const url = urlMatch ? urlMatch[0] : apiData?.URL;

    // headers: match -H 'Key: Value' or --header "Key: Value" or unquoted
    const headerRegex = /(?:-H|--header)\s+(?:'([^']*)'|"([^"]*)"|([^\s]+))/g;
    const headers = {};
    let hmatch;
    while ((hmatch = headerRegex.exec(curl))) {
      const rawHeader = hmatch[1] || hmatch[2] || hmatch[3] || "";
      const parts = rawHeader.split(":");
      const k = (parts.shift() || "").trim();
      const v = parts.join(":").trim();
      if (k) headers[k] = v;
    }

    // data: support '--data' with single/double quotes or unquoted JSON object
    let data = undefined;
    const dataPatterns = [
      /(?:--data-raw|--data-binary|--data|-d)\s+'([\s\S]*?)'/i,
      /(?:--data-raw|--data-binary|--data|-d)\s+"([\s\S]*?)"/i,
      /(?:--data-raw|--data-binary|--data|-d)\s+({[\s\S]*?})/i,
    ];
    for (const pat of dataPatterns) {
      const m = curl.match(pat);
      if (m && m[1]) {
        const raw = m[1];
        try {
          data = JSON.parse(raw);
        } catch (e) {
          data = raw;
        }
        break;
      }
    }
    // as a last resort, try to capture --data without quotes grabbing next token
    if (data === undefined) {
      const m = curl.match(/(?:--data-raw|--data-binary|--data|-d)\s+([^\s]+)/i);
      if (m && m[1]) {
        const raw = m[1];
        try {
          data = JSON.parse(raw);
        } catch (e) {
          data = raw;
        }
      }
    }

    axiosConfig = { method, url, headers: Object.keys(headers).length ? headers : undefined, data };
  } else {
    // Legacy behavior
    if (!apiData?.URL) return [];
    axiosConfig = {
      method: apiData?.method || "get",
      url: apiData?.URL,
      headers: apiData?.header ? JSON.parse(apiData?.header || "{}") : undefined,
      data: apiData?.data ? JSON.parse(apiData?.data || "{}") : undefined,
    };
  }

  // Perform request
  let response = {};
  try {
    // Inject pagination params if provided by caller (best-effort)
    const page = apiData?.page;
    const limit = apiData?.limit;
    if (page !== undefined && axiosConfig) {
      const method = (axiosConfig.method || 'get').toLowerCase();
      // For GET requests, append as query params
      if (method === 'get' && axiosConfig.url) {
        try {
          const u = new URL(axiosConfig.url);
          u.searchParams.set('page', String(page));
          if (limit !== undefined) u.searchParams.set('limit', String(limit));
          axiosConfig.url = u.toString();
        } catch (e) {
          // fallback: append manually
          axiosConfig.url = axiosConfig.url + (axiosConfig.url.includes('?') ? '&' : '?') + `page=${page}` + (limit !== undefined ? `&limit=${limit}` : '');
        }
      } else {
        // For non-GET, merge into body if it's JSON/object
        if (axiosConfig.data && typeof axiosConfig.data === 'object') {
          axiosConfig.data = { ...(axiosConfig.data || {}), page, ...(limit !== undefined ? { limit } : {}) };
        } else if (!axiosConfig.data) {
          axiosConfig.data = { page, ...(limit !== undefined ? { limit } : {}) };
        }
      }
    }
    response = await axios(axiosConfig);
  } catch (err) {
    console.error("fetchFormApi request error", err);
    return [];
  }

  // Normalize response data into an array to be used as options
  const resData = response?.data;
  let items = [];

  const labelKey = apiData?.label || "label";
  const valueKey = apiData?.value || "value";

  if (Array.isArray(resData)) {
    items = resData;
  } else if (resData && typeof resData === "object") {
    // If user specified a response_data_mapping (path), traverse
    const path = apiData?.response_data_mapping;
    if (path) {
      const parts = String(path).split(".").filter(Boolean);
      let cur = resData;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else {
          cur = undefined;
          break;
        }
      }
      if (Array.isArray(cur)) items = cur;
    }

    // If still not found, attempt to discover the first array-valued property
    if (!items.length) {
      for (const k of Object.keys(resData)) {
        if (Array.isArray(resData[k])) {
          items = resData[k];
          break;
        }
      }
    }

    // If still nothing, but resData itself is an object, wrap it
    if (!items.length && resData && typeof resData === "object") {
      items = [resData];
    }
  }

  // Map to { ...item, name, value }
  const formatData = items.map((item) => {
    const name = (item && typeof item === 'object') ? (item[labelKey] ?? item.label ?? item.name ?? item.key ?? JSON.stringify(item)) : String(item);
    const value = (item && typeof item === 'object') ? (item[valueKey] ?? item.value ?? item.id ?? name) : item;
    return { ...item, name, value };
  });

  return formatData;
};

export default fetchFormApi;
